import * as THREE from "three";
import { groundAt, roadLevelAt } from "../terrain";
import { Cell, Grid, SegmentPlacement } from "@highmarch/domain";
import { Palette, cellToWorld } from "../scene";
import { mat, quad, tileMarker } from "./common";
import { OptionBadge } from "./placementMarks";
import { buildTowerModel } from "./towerModel";
import { RigidRig } from "../rigidRig";
import { SeeThrough } from "../seeThrough";
import { badgeMaterial, upgradeBadgeTexture } from "./placementShapes";
import * as MARK from "./placementPalette";
import { OPTION_KEYLINE, OPTION_WIDTH, PreviewHost, ghostBridges, ghostPortal, numberBadge, optionFrame, optionKeyline, ribbon, roadEdge } from "./roadPreview";

/** A tower an Upgrade card can be spent on: its cell and the world height of its roof (for the badge over it). */
export interface UpgradeMark { readonly cell: Cell; readonly top: number }

/** Ghost segments for a picked path card and highlighted valid cells for a picked tower card. */
export class PlacementView {
  readonly group = new THREE.Group();
  /** False under Reduced effects: the marks hold still (no marching edge, no breathing cells) but stay as bright. */
  static motion = true;

  clear(): void {
    SeeThrough.setPoints([]);
    if (this.preview) { RigidRig.dispose(this.preview); this.preview = null; }
    for (const x of this.owned.splice(0)) x.dispose();
    this.group.clear();
  }
  private preview: THREE.Object3D | null = null;
  /**
   * What the current preview made for itself — ribbon and ring geometry, the ghosts' see-through material copies —
   * and `clear` disposes. Everything else (the frames, the overlay materials, the library's models) is shared.
   */
  private readonly owned: { dispose(): void }[] = [];
  private own<T extends { dispose(): void }>(x: T): T { this.owned.push(x); return x; }
  /** What the road preview's marks (roadPreview.ts) draw into and own. */
  private readonly host: PreviewHost = { group: this.group, own: (x) => this.own(x), ghostify: (root) => this.ghostify(root), motion: () => PlacementView.motion };

  /** Turns a library model into a see-through ghost: its own copies of the materials, disposed with the preview. */
  private ghostify(root: THREE.Object3D): void {
    const ghost = (x: THREE.Material) => { const c = this.own(x.clone()); c.transparent = true; c.opacity = PlacementView.GhostOpacity; c.depthWrite = false; return c; };
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      // a worn skin kit's props draw through their own material (bone atlas, GPU motion): a plain see-through copy
      // would place them wrong, so the ghost is the tower's body alone
      if (o.userData.tdKit === true) { o.visible = false; return; }
      m.castShadow = false;
      m.material = Array.isArray(m.material) ? m.material.map(ghost) : ghost(m.material);
    });
  }

  /**
   * Choosing where a tower goes: the legal cells as frames, and on the chosen one the tower itself as a see-through
   * ghost with its range ring and a gold frame, so the player sees exactly what will be built where before it is.
   */
  showTowerSpot(cells: readonly Cell[], chosen: Cell | null, archId: string, range: number, existing: readonly { readonly cell: Cell; readonly range: number }[] = [], minRange = 0): void {
    // Once a spot is chosen the other cells step back (still there to tap, no longer shouting), so the chosen one is
    // the only bright thing on the map.
    this.showValidCells(chosen ? cells.filter((c) => !c.equals(chosen)) : cells, chosen !== null);
    SeeThrough.setPoints(cells.map((c) => cellToWorld(c, groundAt(c.x, c.y))));
    this.existingRanges(existing);
    if (!chosen) return;
    const at = cellToWorld(chosen, groundAt(chosen.x, chosen.y));
    const model = buildTowerModel(archId, false, 1).storeys;
    this.ghostify(model);
    model.position.copy(at);
    this.preview = model;
    const ring = new THREE.Mesh(this.own(new THREE.RingGeometry(Math.max(0.05, range - 0.07), range, 72)), MARK.spotRing);
    const disc = new THREE.Mesh(this.own(new THREE.CircleGeometry(range, 72)), MARK.spotDisc);
    const keyline = new THREE.Mesh(MARK.keylineGeo, MARK.spotKeyline);
    const frame = new THREE.Mesh(MARK.spotFrameGeo, MARK.spotFrame);
    const fill = new THREE.Mesh(MARK.spotFillGeo, MARK.spotFill);
    [disc, ring, fill, keyline, frame].forEach((m, i) => { m.rotation.x = -Math.PI / 2; m.position.copy(at).setY(at.y + 0.035); m.renderOrder = 8 + i; });
    const tilt = PlacementView.slopeTilt(chosen);
    for (const m of [fill, keyline, frame]) m.quaternion.copy(tilt);
    keyline.scale.set(1.04, 1.04, 1);
    // The chosen frame beats a little larger than a cell, so the eye finds it at once among the dimmed ones.
    frame.onBeforeRender = () => {
      const k = PlacementView.motion ? 0.5 + 0.5 * Math.sin(performance.now() / PlacementView.PulseMs) : 1;
      const sc = 1 + PlacementView.SpotBeat * k;
      frame.scale.set(sc, sc, 1); keyline.scale.set(1.04 * sc, 1.04 * sc, 1);
    };
    this.group.add(disc, ring, fill, keyline, frame, model);
    this.blindRing(at, minRange, 8.5);
  }

  /**
   * The Mortar's blind spot (TOWERS_V2 minRange): inside it the range disc turns red and a dashed red rim marks where
   * its shells start to reach, so a player placing it hard by the road sees the road it will never hit.
   */
  private blindRing(at: THREE.Vector3, minRange: number, order: number): void {
    if (!(minRange > 0)) return;
    const disc = new THREE.Mesh(this.own(new THREE.CircleGeometry(minRange, 48)), MARK.blindDisc);
    const rim = new THREE.Mesh(this.own(PlacementView.dashedRing(minRange, 0.06, PlacementView.BlindDashes)), MARK.blindRim);
    for (const m of [disc, rim]) { m.rotation.x = -Math.PI / 2; m.position.copy(at).setY(at.y + 0.04); m.renderOrder = order; }
    rim.renderOrder = order + 0.1;
    this.group.add(disc, rim);
  }
  /** Dashes round the blind ring. */
  static readonly BlindDashes = 24;

  /** A flat ring of `n` dashes (half on, half off) at `radius`, `width` wide. */
  static dashedRing(radius: number, width: number, n: number): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    const arc = (Math.PI * 2) / n;
    for (let i = 0; i < n; i++) parts.push(new THREE.RingGeometry(Math.max(0.01, radius - width), radius, 4, 1, i * arc, arc * 0.55));
    const pos: number[] = [], idx: number[] = [];
    for (const g of parts) {
      const base = pos.length / 3, p = g.getAttribute("position");
      for (let k = 0; k < p.count; k++) pos.push(p.getX(k), p.getY(k), p.getZ(k));
      const ix = g.getIndex()!;
      for (let k = 0; k < ix.count; k++) idx.push(base + ix.getX(k));
      g.dispose();
    }
    const out = new THREE.BufferGeometry();
    out.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    out.setIndex(idx);
    return out;
  }
  /**
   * An Upgrade card in hand. Every tower it can raise wears a pulsing gold frame with a gold up-badge over its roof;
   * towers already at max tier get a dim frame and no badge, so the player sees why they are left out. Once one is
   * chosen the others step back and it beats larger, with the range it will have (gold ring) over the range it has
   * (faint rim), so what the card buys is visible on the map as well as in the tray.
   */
  showUpgradeTargets(targets: readonly UpgradeMark[], maxed: readonly Cell[], chosen: UpgradeMark | null, ranges: { readonly now: number; readonly next: number } | null, badges = true): void {
    this.clear();
    const frames: THREE.Mesh[] = [];
    const flat = (m: THREE.Mesh, c: Cell, lift: number, order: number) => { m.rotation.x = -Math.PI / 2; m.position.copy(cellToWorld(c, groundAt(c.x, c.y) + lift)); m.renderOrder = order; this.group.add(m); return m; };
    for (const c of maxed) { flat(new THREE.Mesh(MARK.keylineGeo, MARK.dimmed.keyline), c, 0.03, 5); flat(new THREE.Mesh(MARK.frameGeo, MARK.dimmed.frame), c, 0.03, 6); }
    for (const m of targets) {
      if (chosen && m.cell.equals(chosen.cell)) continue;
      flat(new THREE.Mesh(MARK.keylineGeo, chosen ? MARK.dimmed.keyline : MARK.validKeyline), m.cell, 0.03, 5);
      frames.push(flat(new THREE.Mesh(MARK.frameGeo, chosen ? MARK.upgradeDim : MARK.upgradeFrame), m.cell, 0.03, 6));
      if (badges) this.upgradeBadge(m, false);
    }
    if (frames[0] && !chosen) frames[0].onBeforeRender = () => {
      const k = PlacementView.motion ? 0.5 + 0.5 * Math.sin(performance.now() / PlacementView.PulseMs) : 1;
      MARK.upgradeFrame.opacity = 0.7 + 0.3 * k;
      const sc = 1 + PlacementView.UpgradeBeat * k;
      for (const f of frames) f.scale.set(sc, sc, 1);
    };
    if (!chosen) return;
    const c = chosen.cell;
    if (ranges) {
      const now = new THREE.Mesh(this.own(new THREE.RingGeometry(Math.max(0.05, ranges.now - 0.05), ranges.now, 72)), MARK.upgradeNowRim);
      const next = new THREE.Mesh(this.own(new THREE.RingGeometry(Math.max(0.05, ranges.next - 0.07), ranges.next, 72)), MARK.spotRing);
      const disc = new THREE.Mesh(this.own(new THREE.CircleGeometry(ranges.next, 72)), MARK.spotDisc);
      flat(disc, c, 0.035, 8); flat(now, c, 0.035, 9); flat(next, c, 0.035, 10);
    }
    flat(new THREE.Mesh(MARK.spotFillGeo, MARK.spotFill), c, 0.035, 11);
    const keyline = flat(new THREE.Mesh(MARK.keylineGeo, MARK.validKeyline), c, 0.035, 12);
    const frame = flat(new THREE.Mesh(MARK.spotFrameGeo, MARK.spotFrame), c, 0.035, 13);
    frame.onBeforeRender = () => {
      const k = PlacementView.motion ? 0.5 + 0.5 * Math.sin(performance.now() / PlacementView.PulseMs) : 1;
      const sc = 1 + PlacementView.SpotBeat * k;
      frame.scale.set(sc, sc, 1); keyline.scale.set(1.04 * sc, 1.04 * sc, 1);
    };
    if (badges) this.upgradeBadge(chosen, true);
  }
  /** How much the upgradeable towers' gold frames swell at the top of their beat. */
  static readonly UpgradeBeat = 0.08;
  /** How far over a tower's roof its up-badge floats (world units). */
  static readonly UpgradeBadgeLift = 0.35;
  /**
   * The badge never floats higher than this over the tower's cell (world units): the fortress's keep stands tall and
   * behind its gate cell, and a badge over its spire hung in the air a long way from the frame it belongs to.
   */
  static readonly UpgradeBadgeMaxHeight = 2;

  /** The gold up-chevron over a tower the card can raise; the chosen one's is larger. Drawn over everything. */
  private upgradeBadge(m: UpgradeMark, chosen: boolean): void {
    const map = upgradeBadgeTexture(chosen);
    if (!map) return;
    const sprite = new THREE.Sprite(badgeMaterial(map));
    const size = OptionBadge.Size * (chosen ? 1.2 : 0.85);
    sprite.scale.set(size, size, 1);
    const ground = groundAt(m.cell.x, m.cell.y);
    sprite.position.copy(cellToWorld(m.cell, Math.min(m.top, ground + PlacementView.UpgradeBadgeMaxHeight) + PlacementView.UpgradeBadgeLift));
    sprite.renderOrder = 14;
    this.group.add(sprite);
  }

  /** How much bigger the chosen spot's frame swells at the top of its beat. */
  static readonly SpotBeat = 0.1;
  /** How solid the placement ghost is: clearly there, clearly not built yet. */
  static readonly GhostOpacity = 0.6;

  /**
   * The road card's placement. The chosen option is painted into the ground as the road it will be
   * (GroundMesh.setPreview, pulsed by the level), with each new tile's marker on it and, over a chasm, a see-through
   * ghost of the bridge it will build. The other options are thin white routes from the open end, so Rotate's choices
   * read as routes rather than as loose squares; they are drawn over the terrain (no depth test or fog) and follow its
   * height, so a bench cannot hide them.
   */
  showGhost(ghosts: readonly SegmentPlacement[], index: number, from: Cell | null = null, grid: Grid | null = null): void {
    this.clear();
    const chosen = ghosts[index];
    ghosts.forEach((g, i) => {
      if (i === index) return;
      const cells = from ? [from, ...g.cells] : g.cells;
      // a dark keyline under the white route, so it still reads on the pale ashlands and marsh ground
      const key = ribbon(cells, OPTION_WIDTH + OPTION_KEYLINE, optionKeyline, 2);
      const route = ribbon(cells, OPTION_WIDTH, optionFrame, 3);
      this.own(key.geometry); this.own(route.geometry);
      this.group.add(key, route);
      // (the other options stay thin white routes: a hint that Rotate has more, not a second road)
    });
    if (!chosen) return;
    // The chosen option itself is painted into the ground as road (GroundMesh.setPreview). Paint cannot show over a
    // chasm, so a crossing gets the plank bridge it will build, as a see-through ghost at bank height.
    if (grid && from) ghostBridges(this.host, [from, ...chosen.cells], grid);
    chosen.cells.forEach((c) => {
      const marker = tileMarker(chosen.tileKind);
      if (marker) { marker.position.copy(cellToWorld(c, roadLevelAt(c.x, c.y) + 0.04)); this.group.add(marker); }
    });
    roadEdge(this.host, from, chosen.cells, grid);
    ghostPortal(this.host, from, chosen.cells);
    if (ghosts.length > 1) ghosts.forEach((g, i) => numberBadge(this.host, g.cells[g.cells.length - 1], i + 1, i === index));
  }

  /**
   * Where a tower may go. Green on green grass was the problem: a green frame over a green fill on a meadow is
   * low contrast whatever its opacity. Each legal cell is a thick WHITE frame with a dark keyline outside it (so it
   * reads on grass, road, rock and haze alike) over a bright green fill, with a white build-here plus in the middle
   * that breathes: the owner found the frames alone "still not clear enough" at phone zoom, and a mark that moves
   * is found before one that is merely bright. Everything is drawn over the terrain (no depth test, fog or tone
   * mapping), so no slope or haze can swallow it. `dim` is the quieter set shown beside a chosen spot.
   */
  showValidCells(cells: readonly Cell[], dim = false): void {
    this.clear();
    const M = dim ? MARK.dimmed : MARK.valid;
    const pluses: THREE.Mesh[] = [];
    for (const c of cells) {
      const at = cellToWorld(c, groundAt(c.x, c.y) + 0.03);
      const tilt = PlacementView.slopeTilt(c);
      const base = new THREE.Mesh(MARK.plotGeo, M.base);
      const rim = new THREE.Mesh(MARK.plotRimGeo, M.rim);
      const badge = new THREE.Mesh(MARK.badgeGeo, M.badge);
      const plus = new THREE.Mesh(MARK.plusGeo, M.plus);
      const marks = dim ? [base, rim, badge, plus] : [base, new THREE.Mesh(MARK.plotKeyGeo, MARK.valid.keyline), rim, badge, plus];
      marks.forEach((m, i) => { m.quaternion.copy(tilt); m.position.copy(at); m.renderOrder = 5 + i * 0.1; this.group.add(m); });
      plus.rotateZ(Math.PI / 4);   // the play camera looks down a diagonal (45° steps): turned, the plus reads "+", not "×"
      pluses.push(badge, plus);
    }
    // Only the badge breathes (a small beat, never the fill's opacity: that read as a smear over the grass).
    const first = this.group.children[0];
    if (first && !dim) first.onBeforeRender = () => {
      const k = PlacementView.motion ? 0.5 + 0.5 * Math.sin(performance.now() / PlacementView.PulseMs) : 1;
      const sc = 1 + PlacementView.PlusBeat * k;
      for (const m of pluses) m.scale.set(sc, sc, 1);
    };
  }
  /**
   * A flat mark's orientation on cell `c`: lying on the ground's slope there (sampled across the plot), so on a bank
   * it keeps its square shape on screen instead of shearing into the hill.
   */
  static slopeTilt(c: Cell): THREE.Quaternion {
    const d = 0.3, x = c.x, y = c.y;
    const sx = (groundAt(x + d, y) - groundAt(x - d, y)) / (2 * d), sz = (groundAt(x, y + d) - groundAt(x, y - d)) / (2 * d);
    const n = new THREE.Vector3(-sx, 1, -sz).normalize();
    const flat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
    return new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), n).multiply(flat);
  }
  /**
   * The rally flag armed (HUD flag button): every road cell the troops can stand on, in a quiet gold frame over a
   * breathing gold fill, so the player sees where a tap plants the flag. No build-here plus: these are not tower spots.
   */
  showRallyCells(cells: readonly Cell[]): void {
    this.clear();
    for (const c of cells) {
      const at = cellToWorld(c, groundAt(c.x, c.y) + 0.03);
      const fill = new THREE.Mesh(MARK.spotFillGeo, MARK.rallyFill);
      const frame = new THREE.Mesh(MARK.frameGeo, MARK.rallyFrame);
      [fill, frame].forEach((m, i) => { m.rotation.x = -Math.PI / 2; m.position.copy(at); m.renderOrder = 5 + i; this.group.add(m); });
    }
    const first = this.group.children[0];
    if (first) first.onBeforeRender = () => {
      const k = PlacementView.motion ? 0.5 + 0.5 * Math.sin(performance.now() / PlacementView.PulseMs) : 1;
      MARK.rallyFill.opacity = PlacementView.RallyFillLow + (PlacementView.RallyFillHigh - PlacementView.RallyFillLow) * k;
    };
  }
  /** The rally cells' fill opacity at the bottom and top of its breath: a hint over the road, never a wall of gold. */
  static readonly RallyFillLow = 0.12;
  static readonly RallyFillHigh = 0.3;
  /** How much the build-here plus swells at the top of its breath. */
  static readonly PlusBeat = 0.12;
  /**
   * Pulse period of the placement highlights, in ms per radian (~1.4 s a cycle): the legal cells here, and the road
   * preview painted into the ground (LevelScene.update reads this one).
   */
  static readonly PulseMs = 220;
  /**
   * The towers already built, while a tower card is placed (QoL P5): a faint white rim at each one's range, so the
   * stretches of road nothing covers yet show at a glance. One thin ring each, no disc: they must not compete with
   * the legal cells or the chosen spot's gold ring.
   */
  private existingRanges(existing: readonly { readonly cell: Cell; readonly range: number }[]): void {
    for (const t of existing) {
      if (!(t.range > 0)) continue;
      const rim = new THREE.Mesh(this.own(new THREE.RingGeometry(Math.max(0.05, t.range - PlacementView.ExistingRimWidth), t.range, 64)), MARK.existingRim);
      rim.rotation.x = -Math.PI / 2;
      rim.position.copy(cellToWorld(t.cell, groundAt(t.cell.x, t.cell.y) + 0.03));
      rim.renderOrder = 7;
      this.group.add(rim);
    }
  }
  /** The existing towers' range rims: world units wide, and faint. */
  static readonly ExistingRimWidth = 0.05;

  /** Selected tower: a translucent disc of its range plus a bright rim. */
  showRange(cell: Cell, range: number, minRange = 0): void {
    this.clear();
    const disc = new THREE.Mesh(this.own(new THREE.CircleGeometry(range, 48)), MARK.rangeDisc);
    disc.rotation.x = -Math.PI / 2;
    disc.position.copy(cellToWorld(cell, groundAt(cell.x, cell.y) + 0.015));
    const rim = new THREE.Mesh(this.own(new THREE.RingGeometry(Math.max(0.05, range - 0.08), range, 64)), MARK.rangeRim);
    rim.rotation.x = -Math.PI / 2;
    rim.position.copy(cellToWorld(cell, groundAt(cell.x, cell.y) + 0.02));
    this.group.add(disc, rim, this.quadAt(cell, 0.95, Palette.ghostEnd));
    this.blindRing(cellToWorld(cell, groundAt(cell.x, cell.y)), minRange, 3);
  }

  private quadAt(c: Cell, size: number, color: number): THREE.Mesh {
    const m = new THREE.Mesh(quad, mat(color, true));
    m.rotation.x = -Math.PI / 2;
    m.position.copy(cellToWorld(c, groundAt(c.x, c.y) + 0.02));
    m.scale.set(size, size, 1);
    return m;
  }

  static ghostAt(ghosts: readonly SegmentPlacement[], cell: Cell): number {
    for (let i = 0; i < ghosts.length; i++) if (ghosts[i].cells[ghosts[i].cells.length - 1].equals(cell)) return i;
    for (let i = 0; i < ghosts.length; i++) if (ghosts[i].cells.some((c) => c.equals(cell))) return i;
    return -1;
  }
}
